import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { MdLock, MdMarkEmailUnread } from 'react-icons/md';
import saApi from '../../services/superAdminApi';
import useSuperAuth from '../../hooks/useSuperAuth';

export default function SuperLogin() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  // Two-factor step
  const [step,          setStep]          = useState('password'); // 'password' | 'otp'
  const [otpToken,      setOtpToken]      = useState('');
  const [code,          setCode]          = useState('');
  const [verifying,     setVerifying]     = useState(false);
  const [resending,     setResending]     = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  const { loginSA } = useSuperAuth();
  const navigate    = useNavigate();

  // Resend cooldown ticker (runs only on the OTP step).
  useEffect(() => {
    if (step !== 'otp') return undefined;
    const t = setInterval(() => setResendSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [step]);

  // ── Step 1: email + password → request a code ──
  const handlePassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await saApi.post('/api/super-admin/auth/login', { email, password });
      if (data.otpRequired) {
        setOtpToken(data.otpToken);
        setCode('');
        setError('');
        setResendSeconds(60);
        setStep('otp');
      } else if (data.token) {
        // Safety fallback — 2FA should always be required, but never strand the user.
        loginSA(data.superAdmin, data.token);
        navigate('/superadmin/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: verify the emailed code → issue the SA token ──
  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError('');
    setVerifying(true);
    try {
      const { data } = await saApi.post('/api/super-admin/auth/verify-otp', { otpToken, code });
      loginSA(data.superAdmin, data.token);
      setTimeout(() => navigate('/superadmin/dashboard', { replace: true }), 100);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendSeconds > 0 || resending) return;
    setError('');
    setResending(true);
    try {
      await saApi.post('/api/super-admin/auth/resend-otp', { otpToken });
      setResendSeconds(60);
    } catch (err) {
      const s = err.response?.data?.secondsLeft;
      if (s) setResendSeconds(s);
      else setError(err.response?.data?.message || 'Could not resend code.');
    } finally {
      setResending(false);
    }
  };

  const backToPassword = () => {
    setStep('password');
    setError('');
    setCode('');
    setOtpToken('');
  };

  const inputCls = `
    w-full px-4 py-3 rounded-xl text-sm outline-none transition-all
    placeholder-[#6b6560] text-[#ede8e0]
  `;
  const inputStyle = { background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' };
  const focusOn  = (e) => (e.target.style.borderColor = 'rgba(237,232,224,0.15)');
  const focusOff = (e) => (e.target.style.borderColor = 'rgba(237,232,224,0.08)');

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#141312' }}>
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#c8102e' }}>
            {step === 'otp' ? <MdMarkEmailUnread className="text-white text-lg" /> : <MdLock className="text-white text-lg" />}
          </div>
          <span className="text-[#ede8e0] font-semibold text-xl tracking-tight">TrainAxis</span>
          <div className="w-8 h-px mt-3 mb-3" style={{ background: 'rgba(237,232,224,0.08)' }} />
          <h1 className="text-[#ede8e0] font-semibold text-lg">{step === 'otp' ? 'Enter your code' : 'Platform Access'}</h1>
          <p className="text-xs mt-1 text-center" style={{ color: '#8a847c' }}>
            {step === 'otp'
              ? <>We emailed a 6-digit code to <span className="text-[#ede8e0]">{email}</span></>
              : 'Sign in to manage'}
          </p>
        </div>

        {step === 'password' ? (
          /* ── Step 1 ── */
          <form onSubmit={handlePassword} className="space-y-3">
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email" required autoComplete="email"
              className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff}
            />
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Password" required autoComplete="current-password"
                className={inputCls + ' pr-11'} style={inputStyle} onFocus={focusOn} onBlur={focusOff}
              />
              <button
                type="button" onClick={() => setShowPwd(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#6b6560' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#8a847c')}
                onMouseLeave={e => (e.currentTarget.style.color = '#6b6560')}
              >
                {showPwd ? <FaEyeSlash className="text-sm" /> : <FaEye className="text-sm" />}
              </button>
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full py-3 rounded-full font-medium text-sm transition-all duration-200 mt-1 flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: '#c8102e', color: '#ffffff' }}
              onMouseEnter={e => !loading && (e.currentTarget.style.background = '#e01535')}
              onMouseLeave={e => (e.currentTarget.style.background = '#c8102e')}
            >
              {loading ? <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : 'Continue'}
            </button>
          </form>
        ) : (
          /* ── Step 2 ── */
          <form onSubmit={handleVerify} className="space-y-3">
            <input
              type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
              value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              placeholder="••••••" maxLength={6}
              className="w-full px-4 py-3 rounded-xl outline-none transition-all text-[#ede8e0] text-center"
              style={{ ...inputStyle, fontSize: 24, letterSpacing: '0.5em', fontFamily: 'monospace' }}
              onFocus={focusOn} onBlur={focusOff}
            />
            <button
              type="submit" disabled={verifying || code.length < 6}
              className="w-full py-3 rounded-full font-medium text-sm transition-all duration-200 mt-1 flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: '#c8102e', color: '#ffffff' }}
              onMouseEnter={e => !verifying && (e.currentTarget.style.background = '#e01535')}
              onMouseLeave={e => (e.currentTarget.style.background = '#c8102e')}
            >
              {verifying ? <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : 'Verify & sign in'}
            </button>

            <div className="flex items-center justify-between pt-1">
              <button
                type="button" onClick={backToPassword}
                className="text-xs transition-colors" style={{ color: '#8a847c' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#c5bfb5')}
                onMouseLeave={e => (e.currentTarget.style.color = '#8a847c')}
              >
                ← Back
              </button>
              <button
                type="button" onClick={handleResend} disabled={resendSeconds > 0 || resending}
                className="text-xs transition-colors disabled:cursor-default"
                style={{ color: resendSeconds > 0 ? '#6b6560' : '#c8102e' }}
              >
                {resending ? 'Sending…' : resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {/* Error */}
        {error && (
          <p className="mt-3 text-xs text-center" style={{ color: '#ff453a' }}>{error}</p>
        )}

        {/* Footer */}
        <p className="mt-10 text-center text-xs uppercase tracking-widest font-medium" style={{ color: '#6b6560' }}>
          Authorized Personnel Only
        </p>

      </div>
    </div>
  );
}
